import Button from '@/Components/Button/Button';
import IndeterminateCheckbox from '@/Components/Checkbox/InditerminateCheckbox';
import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import NoRecordsRow from '@/Components/Table/NoRecordsRow';
import NewWordStatus from '@/Models/NewWords/NewWordStatus';
import type NewWordsStore from '@/Stores/NewWords/NewWordsStore';
import GenericStyles from '@/Style/GenericTable.module.scss';
import {
    createColumnHelper,
    flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type NewWord from '../../Models/NewWords/NewWord';
import Styles from './NewWordTable.module.scss';
import { DobTableWrapper } from '@dedalusdiit/dob-components';
export interface NewWordsGridProps {
    store: NewWordsStore;
}

const {
    tabledataOverview: tableDataOverviewStyle,
    tableFooter: tableFooterStyle,
    footerActions: footerActionsStyle,
    pagination: paginationStyle,
} = GenericStyles;

const NewWordTable = observer((props: NewWordsGridProps): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;
    const { sorting, pagination, rowSelection, newWords, columnVisibility } = store.state;

    const columnHelper = useMemo(() => createColumnHelper<NewWord>(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('ID', {
            header: ({ table }) => (
                <IndeterminateCheckbox
                    {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                />
            ),
            cell: ({ row }) => (
                <div className="px-1">
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                </div>
            ),
        }),
        columnHelper.accessor('Word', {
            id: 'Word',
            header: t('Word'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('Language', {
            id: 'Language',
            header: t('Language'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('LastUsed', {
            id: 'LastUsed',
            header: t('LastUsed'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('LastModification', {
            id: 'LastModification',
            header: t('LastModification'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('Owner', {
            id: 'Owner',
            header: t('User'),
            cell: (info) => info.getValue().FullName,
        }),
    ], []);

    const table = useReactTable({
        columns,
        data: newWords,
        getRowId: (row) => row.ID.toString(),
        rowCount: store.state.wordCount,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableRowSelection: true,
        onRowSelectionChange: (updater) => {
            const newVal = updater instanceof Function ? updater(rowSelection) : updater;
            store.setRowSelection(newVal);
        },
        manualPagination: true,
        onPaginationChange: (updater) => {
            const newVal = updater instanceof Function ? updater(pagination) : updater;
            store.setPagination(newVal);
        },
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: (updater) => {
            const newVal = updater instanceof Function ? updater(sorting) : updater;
            store.setSorting(newVal);
        },
        state: {
            sorting,
            pagination,
            rowSelection,
            columnVisibility,
        },
    });

    store.state.table = table;

    return (
        <div className={tableDataOverviewStyle}>
            <DobTableWrapper color="primary">
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id}>
                                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {store.loading && (
                            <tr>
                                <td>
                                    <CircularProgress />
                                </td>
                            </tr>
                        )}
                        {!store.loading && table.getRowModel().rows.length === 0 && <NoRecordsRow colSpan={columns.length} />}
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={(_) => store.setSelectedWordAndOpenDialog(row.original)}

                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DobTableWrapper>

            <div className={tableFooterStyle}>
                <div className={footerActionsStyle}>
                    <span>
                        {Math.min(
                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                            table.getRowCount(),
                        )}
                        {' '}
                        from
                        {' '}
                        {table.getRowCount()}
                        {' '}
                        Results
                    </span>
                    <Button silent smallPadding mediumGreen onDark onClick={store.reload}>
                        <div>
                            <span className="material-symbols-outlined ">refresh</span>
                            {' '}
                            <span>Reload</span>
                        </div>
                    </Button>
                </div>
                <select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                    {[10, 25, 50, 100].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                            {pageSize}
                        </option>
                    ))}
                </select>
                <div className={paginationStyle}>
                    <Button onDark small silent onClick={() => table.firstPage()}>
                        <span className="material-symbols-outlined ">keyboard_double_arrow_left</span>
                    </Button>
                    <Button onDark small silent onClick={() => table.previousPage()}>
                        <span className="material-symbols-outlined ">keyboard_arrow_left</span>
                    </Button>
                    <div>{table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</div>
                    <Button onDark small silent onClick={() => table.getCanNextPage() ? table.nextPage() : null}>
                        <span className="material-symbols-outlined ">keyboard_arrow_right</span>
                    </Button>
                    <Button onDark small silent onClick={() => table.getCanNextPage() ? table.lastPage() : null}>
                        <span className="material-symbols-outlined ">keyboard_double_arrow_Right</span>
                    </Button>
                </div>
            </div>
            <div className={Styles.buttons}>
                {store.state.selectedNewWordsState !== NewWordStatus.Approved && (
                    <Button
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => {
                            store.setSelectedWords(table.getSelectedRowModel().rows.map((row) => row.original)); store.approveAllSelectedWords();
                        }}
                    >
                        {t('Bulk')} {t('Approve')}
                    </Button>
                )}
                {store.state.selectedNewWordsState !== NewWordStatus.Ignored && (
                    <Button
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => {
                            store.setSelectedWords(table.getSelectedRowModel().rows.map((row) => row.original)); store.ignoreAllSelectedWords();
                        }}
                    >
                        {t('Bulk')} {t('Ignore')}
                    </Button>
                )}
            </div>
        </div>
    );
});
export default NewWordTable;
